import {
  Cancel as CancelIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  DriveFileMoveOutlined as MoveIcon,
  Restore as RestoreIcon,
} from "@mui/icons-material";
import {
  Box,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import type { FC } from "react";
import { useCallback, useState } from "react";
import type { ProductResponse } from "../../../admin/products/productsApi";
import { useProducts } from "../../../admin/products/useProducts";
import DatePicker from "../../../components/datePicker/DatePicker";
import ProductName from "../../../components/productsTable/ProductName";
import TimePicker from "../../../components/timePicker/TimePicker";
import type { Currency } from "../../../data/BaseEntity";
import dayjs from "../../../dayjs";
import type { Price } from "../../../misc/price";
import ProductOptionsWithPrice from "../../../sharedComponents/products/ProductOptionsWithPrice";
import ProductPrice from "../../../sharedComponents/products/ProductPrice";
import ProductSelect from "../../../sharedComponents/products/ProductSelect";
import type { SelectedProductOption } from "../../../sharedComponents/products/price/getOptionPrice";
import PriceByDepartmentDialog from "../../invoices/PriceByDepartmentDialog";
import type { OrderProduct } from "../../orderTypes";
import { useDownloadParticipantListSpreadsheet } from "../../participantLists/useDownloadParticipantListSpreadsheet";
import { AssociatedFilesDialog } from "../attachments/AssociatedFilesDialog";
import AddProducts from "./AddProducts";
import CancelProductDialog from "./CancelProductDialog";
import MoveProductDialog from "./MoveProductDialog";
import ProductsTotalPrice from "./ProductsTotalPrice";
import RestoreProductDialog from "./RestoreProductDialog";

const DeleteProductButton: FC<{ onClick: () => void }> = ({ onClick }) => (
  <IconButton color="secondary" onClick={onClick} aria-label="Usuń produkt">
    <DeleteIcon />
  </IconButton>
);

const CancelProductButton: FC<{ onClick: () => void }> = ({ onClick }) => (
  <Tooltip title="Anuluj produkt">
    <IconButton color="warning" onClick={onClick} aria-label="Anuluj produkt">
      <CancelIcon />
    </IconButton>
  </Tooltip>
);

const RestoreProductButton: FC<{ onClick: () => void }> = ({ onClick }) => (
  <Tooltip title="Cofnij anulowanie produktu">
    <IconButton color="primary" onClick={onClick} aria-label="Cofnij anulowanie produktu">
      <RestoreIcon />
    </IconButton>
  </Tooltip>
);

const MoveProductButton: FC<{ onClick: () => void }> = ({ onClick }) => (
  <Tooltip title="Przenieś produkt do innego zamówienia">
    <IconButton onClick={onClick} aria-label="Przenieś produkt">
      <MoveIcon />
    </IconButton>
  </Tooltip>
);

interface ProductDateProps {
  readonly value: string;
  readonly readonly?: boolean;
  readonly onChange: (date: string) => void;
}

const ProductDate: FC<ProductDateProps> = ({ value, readonly, onChange }) => (
  <DatePicker fullWidth size="small" label="Data" readonly={readonly} value={value} onChange={(v) => onChange(v)} />
);

interface ProductTimeProps {
  value: string | undefined;
  readonly?: boolean;
  onChange: (date: string | undefined) => void;
}

const ProductTime: FC<ProductTimeProps> = ({ value, readonly, onChange }) => (
  <TimePicker
    fullWidth
    size="small"
    label="Czas"
    readonly={readonly}
    value={value ?? ""}
    onChange={(v) => onChange(v)}
  />
);

interface BookingNumberProps {
  readonly value: string;
  readonly readonly?: boolean;
  readonly onChange: (bookingNumber: string) => void;
}

const BookingNumber: FC<BookingNumberProps> = ({ value, readonly, onChange }) => (
  <TextField
    label="Numer rezerwacji"
    fullWidth
    size="small"
    slotProps={{ input: { readOnly: readonly } }}
    value={value}
    onChange={(e) => onChange(e.target.value)}
  />
);

interface DownloadParticipantListButtonProps {
  readonly onClick: () => void | Promise<void>;
}

const DownloadParticipantListButton: FC<DownloadParticipantListButtonProps> = ({ onClick }) => (
  <Tooltip title="Pobierz listę uczestników">
    <IconButton onClick={onClick}>
      <DownloadIcon />
    </IconButton>
  </Tooltip>
);

interface ProductNoteProps {
  readonly value: string;
  readonly readonly?: boolean;
  readonly onChange: (note: string) => void;
}

const ProductNote: FC<ProductNoteProps> = ({ value, readonly, onChange }) => (
  <TextField
    label="Notatka"
    fullWidth
    size="small"
    slotProps={{ input: { readOnly: readonly } }}
    value={value}
    onChange={(e) => onChange(e.target.value)}
  />
);

export interface Product {
  readonly id: string;
  readonly options: SelectedProductOption[];
  readonly date: string;
  readonly time?: string;
  readonly bookingNumber?: string;
  readonly note?: string;
  readonly cancelled?: {
    readonly cancelledAt: string;
    readonly cancelledBy: string;
  };
  readonly exchangeRate: number | undefined;
  readonly price: Price;
}

interface ProductRowProps {
  readonly rowIndex: number;
  readonly orderId: string | undefined;
  readonly orderProduct: Product;
  readonly products: ProductResponse[];
  readonly orderCurrency: Currency;
  readonly isOrderHistorical: boolean;
  readonly isOrderCancelled: boolean;
  readonly onChange: (value: OrderProduct, idx: number) => void;
  readonly onDelete: (idx: number) => void;
  readonly onCancel:
    ((productIndexes: number[], bookingNumber: string, cancelledAt: string) => Promise<void>) | undefined;
  readonly onRestore: ((productIndexes: number[], bookingNumber: string) => Promise<void>) | undefined;
  readonly onMoveProduct: ((productIndexes: number[], targetOrderId: string) => Promise<void>) | undefined;
  readonly onOpenCancelDialog: (idx: number) => void;
  readonly onOpenRestoreDialog: (idx: number) => void;
  readonly onOpenMoveDialog: (idx: number) => void;
  readonly onAddFile: (value: FormData) => Promise<void>;
}

const ProductRow: FC<ProductRowProps> = ({
  rowIndex,
  orderId,
  orderProduct,
  products,
  orderCurrency,
  isOrderHistorical,
  isOrderCancelled,
  onChange,
  onDelete,
  onCancel,
  onRestore,
  onMoveProduct,
  onOpenCancelDialog,
  onOpenRestoreDialog,
  onOpenMoveDialog,
  onAddFile,
}) => {
  const { download: downloadParticipantList } = useDownloadParticipantListSpreadsheet(orderId ?? "");
  const product = products.find((p) => p.id === orderProduct.id);
  const productOptions = product?.options ?? [];

  const handleChange = (change: Partial<Product>) => {
    const { id, date, time, bookingNumber, note, options } = orderProduct;
    onChange(
      {
        id,
        date,
        ...(time !== undefined && { time }),
        ...(bookingNumber !== undefined && { bookingNumber }),
        ...(note !== undefined && { note }),
        options,
        ...change,
      } as unknown as OrderProduct,
      rowIndex,
    );
  };

  const participantListRequired = orderProduct.options.some(
    (o) => productOptions.find((po) => po.id === o.id)?.participantListRequired,
  );

  const isCancelled = !!orderProduct.cancelled;
  const isBooked = !!orderProduct.bookingNumber && orderProduct.bookingNumber !== "UNAVAILABLE";
  const canDelete = !isCancelled && !isBooked;
  const canCancel = isBooked && !isCancelled && !!orderId && !!onCancel;
  const canRestore = isCancelled && isBooked && !isOrderCancelled && !!orderId && !!onRestore;
  const canMove = !isCancelled && !!orderId && !!onMoveProduct && !isOrderHistorical;

  return (
    <TableRow
      data-testid="product-row"
      sx={{
        ...(isCancelled && {
          opacity: 0.5,
          backgroundColor: "rgba(0, 0, 0, 0.05)",
        }),
      }}
    >
      <TableCell>
        <Grid container spacing={1}>
          <Grid size={{ xs: 12, lg: 7 }}>
            <ProductDate value={orderProduct.date} readonly={isCancelled} onChange={(date) => handleChange({ date })} />
          </Grid>
          <Grid size={{ xs: 12, lg: 5 }}>
            <ProductTime
              value={orderProduct.time ?? ""}
              readonly={isCancelled}
              onChange={(time) => handleChange({ time })}
            />
          </Grid>
          <Grid size={{ xs: 12, lg: 7 }}>
            <ProductName productDetails={product} providerId={product?.provider.id} detailsPosition="right">
              <ProductSelect
                size="small"
                fullWidth
                value={orderProduct.id}
                products={products}
                readonly={isCancelled}
                onChange={(id) => handleChange({ id })}
              />
            </ProductName>
          </Grid>
          <Grid size={{ xs: 10, lg: 4 }}>
            <BookingNumber
              value={orderProduct.bookingNumber ?? ""}
              readonly={isCancelled}
              onChange={(bookingNumber) => handleChange({ bookingNumber })}
            />
          </Grid>
          {orderId && (
            <Grid
              size={{ xs: 2, lg: 1 }}
              sx={{
                display: "flex",
                flexDirection: "row",
              }}
            >
              <AssociatedFilesDialog
                defaultFileName={orderProduct.bookingNumber ?? ""}
                readonly={isCancelled}
                onAdd={async (selectedFile) => {
                  if (selectedFile) {
                    await onAddFile(selectedFile);
                  }
                }}
              />
              {orderProduct.bookingNumber && participantListRequired && (
                <DownloadParticipantListButton onClick={() => downloadParticipantList(orderProduct.bookingNumber!)} />
              )}
            </Grid>
          )}
          <Grid size={12}>
            <ProductNote
              value={orderProduct.note ?? ""}
              readonly={isCancelled}
              onChange={(note) => handleChange({ note })}
            />
          </Grid>
          {isCancelled && (
            <Grid size={12}>
              <Tooltip title={`Przez: ${orderProduct.cancelled?.cancelledBy}`}>
                <Typography variant="caption" color="textSecondary">
                  Anulowano {dayjs(orderProduct.cancelled?.cancelledAt).format("DD.MM.YYYY HH:mm")}
                </Typography>
              </Tooltip>
            </Grid>
          )}
        </Grid>
      </TableCell>
      <TableCell>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          {canDelete && <DeleteProductButton onClick={() => onDelete(rowIndex)} />}
          {canMove && <MoveProductButton onClick={() => onOpenMoveDialog(rowIndex)} />}
          {canCancel && <CancelProductButton onClick={() => onOpenCancelDialog(rowIndex)} />}
          {canRestore && <RestoreProductButton onClick={() => onOpenRestoreDialog(rowIndex)} />}
        </Box>
      </TableCell>
      <TableCell>
        <ProductOptionsWithPrice
          productId={orderProduct.id}
          productDate={orderProduct.date}
          selectedOptions={orderProduct.options}
          availableOptions={productOptions.map((o) => ({ ...o, info: o.info.polish }))}
          readonly={isCancelled}
          onChange={(options) => handleChange({ options })}
        />
      </TableCell>
      <TableCell>
        <ProductPrice price={orderProduct.price} currency={orderCurrency} />
      </TableCell>
    </TableRow>
  );
};

interface Props {
  readonly orderId: string | undefined;
  readonly values: Product[];
  readonly currency: Currency;
  readonly totalPrice: Price;
  readonly isOrderHistorical: boolean;
  readonly isOrderCancelled: boolean;
  onChange: (value: OrderProduct, idx: number) => void;
  onDelete: (idx: number) => void;
  onCancel: ((productIndexes: number[], bookingNumber: string, cancelledAt: string) => Promise<void>) | undefined;
  onRestore: ((productIndexes: number[], bookingNumber: string) => Promise<void>) | undefined;
  onMoveProduct: ((productIndexes: number[], targetOrderId: string) => Promise<void>) | undefined;
  onAddFromPackage: (values: OrderProduct[], tags: { readonly category: string; readonly value: string }[]) => void;
  onAddFile: (value: FormData) => Promise<void>;
}

const OrderProductsTable: FC<Props> = ({
  orderId,
  values,
  currency,
  totalPrice,
  isOrderHistorical,
  isOrderCancelled,
  onChange,
  onDelete,
  onCancel,
  onRestore,
  onMoveProduct,
  onAddFromPackage,
  onAddFile,
}) => {
  const { products } = useProducts();
  const [cancelDialogRowIndex, setCancelDialogRowIndex] = useState<number | null>(null);
  const [restoreDialogRowIndex, setRestoreDialogRowIndex] = useState<number | null>(null);
  const [moveDialogRowIndex, setMoveDialogRowIndex] = useState<number | null>(null);

  const openCancelDialog = useCallback((idx: number) => setCancelDialogRowIndex(idx), []);
  const openRestoreDialog = useCallback((idx: number) => setRestoreDialogRowIndex(idx), []);
  const openMoveDialog = useCallback((idx: number) => setMoveDialogRowIndex(idx), []);

  return (
    <>
      <Table data-testid="order-products">
        <TableHead>
          <TableRow>
            <TableCell>Produkt</TableCell>
            <TableCell />
            <TableCell>Opcje</TableCell>
            <TableCell>Cena</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {values.map((product, idx) => (
            <ProductRow
              key={`${idx}:${product.id}:${product.date}`}
              rowIndex={idx}
              orderId={orderId}
              orderProduct={product}
              products={products}
              orderCurrency={currency}
              isOrderHistorical={isOrderHistorical}
              isOrderCancelled={isOrderCancelled}
              onChange={onChange}
              onDelete={onDelete}
              onCancel={onCancel}
              onRestore={onRestore}
              onMoveProduct={onMoveProduct}
              onOpenCancelDialog={openCancelDialog}
              onOpenRestoreDialog={openRestoreDialog}
              onOpenMoveDialog={openMoveDialog}
              onAddFile={onAddFile}
            />
          ))}
        </TableBody>
        <TableFooter>
          <TableRow>
            <TableCell>
              <AddProducts values={values} onChange={onChange} onAddFromPackage={onAddFromPackage} />
            </TableCell>
            <TableCell />
            <TableCell align="right">
              <ProductsTotalPrice
                price={totalPrice}
                currency={currency}
                info={<PriceByDepartmentDialog orderId={orderId ?? ""} />}
              />
            </TableCell>
          </TableRow>
        </TableFooter>
      </Table>
      {cancelDialogRowIndex !== null && onCancel && (
        <CancelProductDialog
          open
          products={(() => {
            const triggerProduct = values[cancelDialogRowIndex];
            const bookingNumber = triggerProduct?.bookingNumber ?? "";
            if (!bookingNumber || bookingNumber === "UNAVAILABLE") {
              return [];
            }
            const withSameBooking = values
              .map((p, i) => ({
                index: i,
                product: p,
                name: products.find((pr) => pr.id === p.id)?.name ?? p.id,
                date: p.date,
              }))
              .filter(({ product }) => !product.cancelled && product.bookingNumber === bookingNumber)
              .map(({ index, name, date }) => ({ index, name, date }));
            return withSameBooking;
          })()}
          onConfirm={async (selectedIndexes, cancelledAt) => {
            const bookingNumber = values[cancelDialogRowIndex]?.bookingNumber ?? "";
            await onCancel(selectedIndexes, bookingNumber, cancelledAt);
            setCancelDialogRowIndex(null);
          }}
          onCancel={() => setCancelDialogRowIndex(null)}
        />
      )}
      {restoreDialogRowIndex !== null && onRestore && (
        <RestoreProductDialog
          open
          products={(() => {
            const triggerProduct = values[restoreDialogRowIndex];
            const bookingNumber = triggerProduct?.bookingNumber ?? "";
            if (!bookingNumber || bookingNumber === "UNAVAILABLE") {
              return [];
            }
            return values
              .map((p, i) => ({
                index: i,
                product: p,
                name: products.find((pr) => pr.id === p.id)?.name ?? p.id,
                date: p.date,
              }))
              .filter(({ product }) => !!product.cancelled && product.bookingNumber === bookingNumber)
              .map(({ index, name, date }) => ({ index, name, date }));
          })()}
          onConfirm={async (selectedIndexes) => {
            const bookingNumber = values[restoreDialogRowIndex]?.bookingNumber ?? "";
            await onRestore(selectedIndexes, bookingNumber);
            setRestoreDialogRowIndex(null);
          }}
          onCancel={() => setRestoreDialogRowIndex(null)}
        />
      )}
      {moveDialogRowIndex !== null && onMoveProduct && orderId && (
        <MoveProductDialog
          open
          currentOrderId={orderId}
          products={(() => {
            const triggerProduct = values[moveDialogRowIndex];
            const bookingNumber = triggerProduct?.bookingNumber ?? "";
            const nameAt = (i: number) => products.find((pr) => pr.id === values[i]?.id)?.name ?? values[i]?.id ?? "";
            if (!bookingNumber || bookingNumber === "UNAVAILABLE") {
              return [
                {
                  index: moveDialogRowIndex,
                  name: nameAt(moveDialogRowIndex),
                  date: triggerProduct?.date ?? "",
                },
              ];
            }
            return values
              .map((p, i) => ({
                index: i,
                product: p,
                name: nameAt(i),
                date: p.date,
              }))
              .filter(({ product }) => !product.cancelled && product.bookingNumber === bookingNumber)
              .map(({ index, name, date }) => ({ index, name, date }));
          })()}
          onConfirm={async (selectedIndexes, targetOrderId) => {
            await onMoveProduct(selectedIndexes, targetOrderId);
            setMoveDialogRowIndex(null);
          }}
          onCancel={() => setMoveDialogRowIndex(null)}
        />
      )}
    </>
  );
};

export default OrderProductsTable;
